import React, { memo, useState } from "react";
import CollapsibleTaskSection from "./CollapsibleTaskSection";
import SlaTaskDetailModal from "./SlaTaskDetailModal";
import { SlaActiveFilters, SlaStatus, SlaTaskListItem } from "../types";
import { useSlaTaskList } from "../hooks/useSlaTaskList";

interface Props {
  filters: SlaActiveFilters;
  propertyId?: string;
}

// api-contract D0.5 / react-frontend-tasks F4:
// Each section ("Open SLA Tasks Requiring Attention" and "Completed SLA Tasks")
// has its OWN independent SLA-status filter state and its OWN StatusFilterPills
// instance. Changing the open-section pill does NOT affect the completed section
// and vice versa.
//
// api-contract D5: two independent calls to /v2/sla_dashboard/tasks — one for
// open tasks, one for completed tasks, each with their own pagination and their
// own sla_status filter.
// api-contract D8: collapsibility is local ephemeral state; both expanded by default.
// api-contract D0.6 / Req 2: lifecycle default is "open"; "all" is no longer
// selectable, so only one section is ever visible at a time.
const TasksRequiringAttention = ({ filters, propertyId }: Props) => {
  const [selectedTask, setSelectedTask] = useState<SlaTaskListItem | null>(null);

  // Per-section independent SLA-status filters (api-contract D0.5).
  // null → no `sla_status` param is sent, so the backend applies its
  // per-lifecycle default (open: upcoming/at_risk/breached; completed: all four)
  // per api-contract §1.4. Do NOT hard-code an initial pill value.
  const [openStatus, setOpenStatus] = useState<SlaStatus | null>(null);
  const [completedStatus, setCompletedStatus] = useState<SlaStatus | null>(null);

  // Always call both hooks regardless of visibility — React hooks must not be
  // called conditionally. The backend will only be queried when the hook fires.
  const openList = useSlaTaskList(
    { ...filters, slaStatus: openStatus, taskLifecycleStatus: "open" },
    propertyId
  );
  const completedList = useSlaTaskList(
    { ...filters, slaStatus: completedStatus, taskLifecycleStatus: "completed" },
    propertyId
  );

  // "all" is no longer a selectable value (api-contract D0.6). Fall back
  // defensively to the open section for any unexpected value.
  const lifecycleStatus = filters.taskLifecycleStatus ?? "open";

  const openTitle = propertyId
    ? "Open Tasks Requiring Attention"
    : "Open SLA Tasks Requiring Attention";

  return (
    <div>
      {/* Open section — shown when lifecycle is "open" (default). */}
      {lifecycleStatus === "open" && (
        <CollapsibleTaskSection
          title={openTitle}
          tasks={openList.tasks}
          isLoading={openList.isLoading}
          totalCount={openList.totalCount}
          onTaskClick={setSelectedTask}
          selectedStatus={openStatus}
          onStatusChange={setOpenStatus}
          sectionType="open"
          datasource={openList.datasource}
        />
      )}

      {/* Completed section — shown when lifecycle is "completed". */}
      {lifecycleStatus === "completed" && (
        <CollapsibleTaskSection
          title="Completed SLA Tasks"
          tasks={completedList.tasks}
          isLoading={completedList.isLoading}
          totalCount={completedList.totalCount}
          onTaskClick={setSelectedTask}
          selectedStatus={completedStatus}
          onStatusChange={setCompletedStatus}
          sectionType="completed"
          datasource={completedList.datasource}
        />
      )}

      {/* Fallback — an unexpected lifecycle value still renders the open section. */}
      {lifecycleStatus !== "open" && lifecycleStatus !== "completed" && (
        <CollapsibleTaskSection
          title={openTitle}
          tasks={openList.tasks}
          isLoading={openList.isLoading}
          totalCount={openList.totalCount}
          onTaskClick={setSelectedTask}
          selectedStatus={openStatus}
          onStatusChange={setOpenStatus}
          sectionType="open"
          datasource={openList.datasource}
        />
      )}

      {/* Single modal instance — shared across both sections (api-contract F6) */}
      <SlaTaskDetailModal
        isOpen={!!selectedTask}
        task={selectedTask}
        onClose={() => setSelectedTask(null)}
      />
    </div>
  );
};

export default memo(TasksRequiringAttention);
